import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '../configuration/tokenHandling/prepareHeaders';
import { setIsActivated } from '../features/chat/chatSlice';

export type Token = {
    token: string;
    userId: string;
};

export type ChatMemberType = 'asset' | 'backoffice' | 'driver';

export type Chat = {
    id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    licensePlate?: string;
    type: ChatMemberType;
    tagIds: string[];
};

export type ChatMember = {
    id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    type: ChatMemberType;
    licensePlate?: string;
    tagIds?: string[];
};

export type BroadcastMessage = {
    message: string;
    groups: string[];
};

export type GetChatMemberQuery = {
    chatId: string;
    userId: string;
};

export type TranslatedMessage = {
    chatId: string;
    messageId: number;
    translations: { [key: string]: string };
};

export type GetTranslationQuery = {
    chatId: string;
    messageId: number;
    locale: string;
};

export type GetActivationsQuery = {
    accountId: string;
};

export const chatAdministrationApi = createApi({
    reducerPath: 'chatAdministrationApi',
    baseQuery: fetchBaseQuery({ baseUrl: import.meta.env.VITE_ORDER_COMMUNICATION_CHAT_BACKEND, prepareHeaders }),
    endpoints: builder => ({
        requestToken: builder.mutation<Token, void>({
            query: () => ({
                method: 'POST',
                url: '/token',
                responseHandler: async (response: Response): Promise<Token> => {
                    const data = await response.json();
                    return {
                        token: data.token,
                        userId: data.user_id,
                    };
                },
            }),
        }),
        enterChat: builder.mutation<void, string>({
            query: chatId => ({
                method: 'POST',
                url: `/chats/${chatId}/members`,
            }),
        }),
        getChats: builder.query<Chat[], void>({
            query: () => ({
                url: '/chats',
                responseHandler: async (response: Response): Promise<Chat[]> => {
                    const data = await response.json();
                    const chats: Chat[] = data.items.map(
                        // biome-ignore lint/suspicious/noExplicitAny: Basically a rest user which we dont' have yet
                        (chat: any): Chat => ({
                            id: chat.id,
                            name: chat.name,
                            firstName: chat.first_name,
                            lastName: chat.last_name,
                            licensePlate: chat.license_plate,
                            type: chat.type,
                            tagIds: chat.tag_ids ?? [],
                        })
                    );
                    return chats.sort((a, b) => a.name.localeCompare(b.name));
                },
            }),
        }),
        getChatMember: builder.query<ChatMember, GetChatMemberQuery>({
            query: ({ chatId, userId }) => ({
                url: `/chats/${chatId}/members/${userId}`,
                responseHandler: async (response: Response): Promise<ChatMember> => {
                    const data = await response.json();
                    return {
                        id: data.id,
                        type: data.type,
                        name: data.name,
                        firstName: data.first_name,
                        lastName: data.last_name,
                        licensePlate: data.license_plate,
                        tagIds: data.tag_ids ?? [],
                    };
                },
            }),
        }),
        sendBroadcastMessage: builder.mutation<void, BroadcastMessage>({
            query: ({ message, groups }) => ({
                method: 'POST',
                url: '/broadcasts',
                body: {
                    message,
                    groups,
                },
            }),
        }),
        getTranslations: builder.query<TranslatedMessage, GetTranslationQuery>({
            query: ({ chatId, messageId, locale }) => ({
                url: `/chats/${chatId}/messages/${messageId}/translations?locale=${locale}`,
            }),
        }),
        getChatActivation: builder.query<boolean, GetActivationsQuery>({
            query: ({ accountId }) => ({
                url: `/activations/${accountId}`,
                validateStatus: response => response.status === 200 || response.status === 404,
                responseHandler: async (response: Response): Promise<boolean> => {
                    if (response.status === 404) {
                        return false;
                    }

                    const data = await response.json();
                    return !!data?.activated_at;
                },
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setIsActivated(data));
                } catch (error) {
                    console.error(error);
                }
            },
        }),
    }),
});

export const {
    useRequestTokenMutation,
    useSendBroadcastMessageMutation,
    useEnterChatMutation,
    useGetChatsQuery,
    useGetChatMemberQuery,
    useLazyGetTranslationsQuery,
    useGetChatActivationQuery,
} = chatAdministrationApi;
